import { GridApiPremium, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid-premium';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { MutableRefObject, useCallback } from 'react';

interface ActionProps {
  apiRef: MutableRefObject<GridApiPremium | null>;
}

/**
 * Tag editing for the "edit tags" flows. Each tag on a row carries its own dates, and
 * `addTag` with a tag that is already on the row replaces it, which is how date edits are
 * applied. The dialog diffs the rows against the persisted tags on save, posting the tags
 * that are new or got new dates and deleting the ones that were taken off.
 */
const useAssortmentTagEditActions = ({ apiRef }: ActionProps) => {
  const addTag = useCallback(
    (row: AssortmentItemMinimal, tagToAdd: SupplierItemAssortmentTag) => {
      const existingTags = row.assortmentTags ?? [];
      const updatedTags = existingTags.some((tag) => tag.tagId === tagToAdd.tagId)
        ? existingTags.map((tag) => (tag.tagId === tagToAdd.tagId ? tagToAdd : tag))
        : [...existingTags, tagToAdd];

      apiRef.current?.updateRows([
        { mgSupplierItemId: row.mgSupplierItemId, assortmentTags: updatedTags },
      ]);
    },
    [apiRef]
  );

  const removeTag = useCallback(
    (row: AssortmentItemMinimal, tagToRemove: SupplierItemAssortmentTag) => {
      apiRef.current?.updateRows([
        {
          mgSupplierItemId: row.mgSupplierItemId,
          assortmentTags: row.assortmentTags?.filter((tag) => tag.tagId !== tagToRemove.tagId),
        },
      ]);
    },
    [apiRef]
  );

  const addTagToAllItems = useCallback(
    (tagToAdd: SupplierItemAssortmentTag) => {
      const rowIds = gridFilteredSortedRowIdsSelector(apiRef) ?? [];

      apiRef.current?.updateRows(
        rowIds.map((rowId) => {
          const row = apiRef.current?.getRow(rowId);
          const existingTags: SupplierItemAssortmentTag[] = row.assortmentTags ?? [];

          return {
            mgSupplierItemId: rowId,
            assortmentTags: existingTags.some((tag) => tag.tagId === tagToAdd.tagId)
              ? existingTags.map((tag) => (tag.tagId === tagToAdd.tagId ? tagToAdd : tag))
              : [...existingTags, tagToAdd],
          };
        })
      );
    },
    [apiRef]
  );

  const removeTagFromAllItems = useCallback(
    (tagIdToRemove: number) => {
      const rowIds = gridFilteredSortedRowIdsSelector(apiRef) ?? [];

      apiRef.current?.updateRows(
        rowIds.map((rowId) => {
          const row = apiRef.current?.getRow(rowId);
          const existingTags: SupplierItemAssortmentTag[] = row.assortmentTags ?? [];

          return {
            mgSupplierItemId: rowId,
            assortmentTags: existingTags.filter((tag) => tag.tagId !== tagIdToRemove),
          };
        })
      );
    },
    [apiRef]
  );

  return { addTag, removeTag, addTagToAllItems, removeTagFromAllItems };
};

export default useAssortmentTagEditActions;
